// @givmo/app-charity — the passkey SIGN-IN for the console's director door (AS-WEB12).
//
// `@givmo/passkeys`' `PasskeySignInButton`, shown only where this browser and the backend can run a
// passkey sign-in (`usePasskeySignInAvailability`, the dark gate the employer portal's `/login-home`
// shares), wired to this console's own action (`useAuth().signInWithPasskey`: `@givmo/auth`'s
// UNBOUND assertion over `@givmo/passkeys`' transport) and landing. Board's director door places it
// through the host's `renderWaysIn`, under an "or" beneath the console's Google, Microsoft and email
// ways in (m2-door, m1-signed-out). The console's own `/login-home` keeps its three buttons (the
// 2026-08-11 chooser rail, `funnel-door.test.tsx`).
//
// The landing is the console door's: `returnTo` when it is a safe in-app path, else the root, with
// the sign-in landing state every sign-in carries (`console/sidebar-frame.ts`). The session the
// assertion starts reaches the console's one auth-state chokepoint like every other sign-in (the
// backend `users` row, the suite session) before the guard admits it anywhere.
//
// A sign-in, never a step-up. A failure is this way in's failure and nothing more: it signs nobody
// out and leaves the door's other ways in as they were. A dismissed prompt shows nothing.
//
// ONE exception, the company's sign-in lock (AS-WEB27): the passkey mint's `COMPANY_SSO_REQUIRED`
// (`organizationSsoRequired`) is not this way in's failure but the door's, and the door shows it as
// its one notice — the organization door, Board's line, the way back in — never as this button's red
// line (cut 1007e's review F3, Board's B-F1). A door that gives `onLockRefused` gets the refusal.

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ErrorMessage } from '@givmo/design-system';
import { AuthError } from '@givmo/auth';
import { PasskeySignInButton, usePasskeySignInAvailability } from '@givmo/passkeys';
import { useAuth } from '../../auth/auth-context';
import { SIGN_IN_LANDING_STATE } from '../../console/sidebar-frame';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { DEFAULT_AUTHENTICATED_ROUTE } from './auth-routes';
import { useAuthError } from './use-auth-error';

export interface CharityPasskeySignInProps {
  /** Where a completed sign-in lands: a safe in-app path, else the console's root. */
  returnTo?: string;
  /** Called when the visitor picks this way in, before the sign-in starts. */
  onChosen?: () => void;
  /** Reports the sign-in in flight, so a door can hold its other ways in meanwhile. */
  onPendingChange?: (pending: boolean) => void;
  /** Another way in on the same door is in flight. */
  disabled?: boolean;
  /**
   * The company's sign-in lock refused the passkey sign-in (`organizationSsoRequired`): the door's to
   * show as its lock notice (its `SignInDoorError`), so this button draws no line of its own. Every
   * other failure is still this button's red line.
   */
  onLockRefused?: (thrown: unknown) => void;
}

export function CharityPasskeySignIn({
  returnTo,
  onChosen,
  onPendingChange,
  disabled = false,
  onLockRefused,
}: CharityPasskeySignInProps) {
  const { signInWithPasskey, probePasskeyAvailability } = useAuth();
  const available = usePasskeySignInAvailability(probePasskeyAvailability);
  const navigate = useNavigate();
  const { error, clearError, surface } = useAuthError();
  const [pending, setPending] = useState(false);

  if (!available) return null;

  const setInFlight = (next: boolean) => {
    setPending(next);
    onPendingChange?.(next);
  };

  const onSignIn = async () => {
    clearError();
    onChosen?.();
    setInFlight(true);
    try {
      await signInWithPasskey();
      await navigate({
        to: sanitizeRedirectTarget(returnTo) ?? DEFAULT_AUTHENTICATED_ROUTE,
        state: SIGN_IN_LANDING_STATE,
      });
    } catch (thrown) {
      if (
        onLockRefused !== undefined &&
        AuthError.from(thrown).kind === 'organizationSsoRequired'
      ) {
        onLockRefused(thrown);
      } else {
        surface(thrown);
      }
    } finally {
      setInFlight(false);
    }
  };

  return (
    <div className="flex flex-col gap-12">
      <PasskeySignInButton
        loading={pending}
        disabled={disabled || pending}
        onClick={() => void onSignIn()}
      />
      {error !== null && <ErrorMessage testId="auth-passkey-error">{error}</ErrorMessage>}
    </div>
  );
}
